import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Card } from "components/lib";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import Axios from "axios";
import { get, set, del, keys } from "idb-keyval";
import "survey-core/defaultV2.min.css";
import { v4 as uuidv4 } from "uuid";
import { demoItrForm } from "views/website/demo-itr-form";
const Settings = require("settings.json");
// TFN checksum (8- and 9-digit). Same rule as the server's mirrored copy in
// helper/redactTfn.js, but that copy is NOT in this form's request path: only
// client-group onboarding runs collectTfnValidationErrors server-side
// (controller/formController.js:980). Validation here is the only gate.
const { validateTfn } = require("./australian-identifiers");

/* ────────────────────────── Environment ────────────────────────── */
const server_url = Settings[process.env.NODE_ENV].server_url;
const accountId =
  process.env.NODE_ENV === "production"
    ? "3bdab43d-8760-4b98-8036-e5cd4f4b70a4"
    : "dce2224f-53be-421f-9dcb-f7dbbd6acb96";
StylesManager.applyTheme("defaultV2");

/* ────────────────────────── Theme (trim) ───────────────────────── */
const themeJson = { header: { height: 0 } }; // swap with full theme if desired

/* ─────────────────── Utility + validation helpers ────────────────── */
const usePersistentResponseId = () => {
  const [id, setId] = useState(null);
  useEffect(() => {
    (async () => {
      let stored = await get("mckinleyResponseId");
      if (!stored) {
        stored = uuidv4();
        await set("mckinleyResponseId", stored);
      }
      setId(stored);
    })();
  }, []);
  return id;
};

const validateABN = (abn) => {
  if (abn.length !== 11) return "ABN must be 11 digits";
  abn = (abn.charAt(0) - 1).toString() + abn.substring(1);
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const sum = abn.split("").reduce((a, d, i) => a + d * weights[i], 0);
  return sum % 89 === 0 ? null : "Invalid ABN";
};
const dataURLtoBlob = (dataURL) => {
  const [meta, content] = dataURL.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const binary = atob(content);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new Blob([array], { type: mime });
};

/* ────────────────────────── Component ─────────────────────────── */
export function DemoITRResponse() {
  /* Persistent ID must exist before anything else */
  const responseId = usePersistentResponseId();

  /* UX state */
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const isSubmittingRef = useRef(false);

  /* Build Survey model once we have the ID */
  const model = useMemo(() => new Model(demoItrForm), []);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--campaign-background",
      "#0038cf"
    );

    // Cleanup when component unmounts
    return () => {
      document.documentElement.style.removeProperty("--campaign-background");
    };
  }, []);

  /* ───── Overlay component ───── */
  const Overlay = () => (
    <div className="fixed inset-0 grid place-items-center bg-black/50 backdrop-blur-sm z-50">
      <div className="w-80 rounded-2xl bg-white shadow-2xl p-6 flex flex-col items-center gap-4 animate-pop">
        <h2 className="text-lg font-semibold text-gray-800">Uploading your files…</h2>
        <progress
          value={pct}
          max={100}
          className="w-full h-3 rounded-full overflow-hidden bg-gray-200 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:bg-sg-blue [&::-webkit-progress-value]:rounded-full"
        />
        <span className="text-sm text-gray-600">{pct}%</span>
      </div>
    </div>
  );

  /* ───────────── Restore + autosave draft ───────────── */
  useEffect(() => {
    get("mckinleyDraft").then((d) => d && (model.data = d));
    const save = () => set("mckinleyDraft", model.data);
    model.onValueChanged.add(save);
    return () => model.onValueChanged.remove(save);
  }, [model]);

  /* ───────────── Validation logic ───────────── */
  useEffect(() => {
    model.onValidateQuestion.add((_, ctx) => {
      const { question, value } = ctx;
      if (["tfn", "spouseTfn"].includes(question.name)) ctx.error = !value && question.isRequired ? "TFN is required" : validateTfn(value);
      if (question.name === "abn") ctx.error = !value && question.isRequired ? "ABN is required" : validateABN(value);
    });
  }, [model]);

  /* ───────────── onUploadFiles (single attachment) ───────────── */
  useEffect(() => {
    const uploadHandler = async (sender, options) => {
      const processed = await Promise.all(
        options.files.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = async (e) => {
                try {
                  await set(`${responseId}/${options.name}/${file.name}`, file);
                  resolve({ file, name: file.name, type: file.type, content: e.target.result });
                } catch (err) {
                  reject(err);
                }
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            })
        )
      );
      options.callback("success", processed);
    };
    model.onUploadFiles.add(uploadHandler);
    return () => model.onUploadFiles.remove(uploadHandler);
  }, [model, responseId]);

  /* ───────────── Submission pipeline ───────────── */
  const remoteUpload = async (blob, field, fname) => {
    const fd = new FormData();
    fd.append("file", blob, fname);
    fd.append("response_id", responseId);
    fd.append("key", `${responseId}/${field}/${fname}`);
    fd.append("field_name", field);
    fd.append("file_name", fname);
    const res = await Axios.post(`${server_url}/api/utility/gcp/upload`, fd);
    if (res.status !== 200) throw new Error("File upload failed");
    return { name: fname, type: blob.type || "application/octet-stream", url: res.data.url, key: `${responseId}/${field}/${fname}`};
  };

  const handleUploadsAndSubmit = async (sender) => {
    const data = { ...sender.data };
    const fileQs = sender.getAllQuestions().filter((q) => q.getType() === "file" && q.value?.length);
    const total = fileQs.reduce((s, q) => s + q.value.length, 0);
    let done = 0;

    for (const q of fileQs) {
      const replacements = [];
      for (const f of q.value) {
        const blob = (await get(`${responseId}/${q.name}/${f.name}`)) || dataURLtoBlob(f.content);
        replacements.push(await remoteUpload(blob, q.name, f.name));
        done += 1;
        setPct(Math.round((done / total) * 100));
      }
      data[q.name] = replacements;
    }

    await Axios.post(`/api/public/demo-itr-response/${responseId}`, {
      id: responseId,
      account_id: accountId,
      data,
      form_data: demoItrForm,
    });

    // All files done ➜ hide overlay so completion message is visible
    setBusy(false);
  };

  /* ───────────── onCompleting ───────────── */
  useEffect(() => {
    const completing = async (sender, opts) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      opts.allowComplete = false;
      setBusy(true);
      setPct(0);
      try {
        await handleUploadsAndSubmit(sender);
        await del("mckinleyDraft");
        setBusy(false);
        opts.allowComplete = true;
        sender.doComplete();
        console.log("Response submitted successfully");
        // alert("Your response has been successfully submitted!");
        const allKeys = await keys();
        console.log("Clearing keys:", allKeys);
        for (const k of allKeys) {
          console.log("Deleting key:", k);
          await del(k);
        }

        const titleEl = document.querySelector(".sd-title");
        console.log("Hiding title element:", titleEl);
        // Hide the title element after submission
        // This assumes the title element has a class of "sd-title"
        if (titleEl) titleEl.style.display = "none";

        } catch (err) {
        console.error(err);
        alert(err.message || "Upload failed");
        setBusy(false);
        isSubmittingRef.current = false;
      }
    };
    model.onCompleting.add(completing);
    return () => model.onCompleting.remove(completing);
  }, [model, responseId]);

  /* ───────────── Theme tweak ───────────── */
  useEffect(() => {
    model.applyTheme(themeJson);
    model.onAfterRenderHeader.add((_, opt) => (opt.htmlElement.style.textAlign = "center"));
  }, [model]);

  model.onComplete.add(() => {
    // Clear the draft on successful completion
    del("mckinleyDraft").catch((err) => console.error("Failed to clear draft:", err));
  }); 


    /* Guard render while ID is loading */
  if (!responseId) return null;

  /* ───────────── Render ───────────── */
  return (
    <>
      <div className="min-w-min w-3/4">
        <Grid>
          <Card>
            <img
              src="https://stackgo.io/wp-content/uploads/2020/11/stackgo-logo.png"
              style={{ height: 100, display: "block", margin: "0 auto" }}
              alt="McKinley Plowman"
            />
            <Survey model={model} />
          </Card>
        </Grid>
      </div>
      {busy && <Overlay />}
    </>
  );
}
